import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faArrowsMaximize,
  faCodeBranch,
  faEarthAmericas,
  faHexagonXmark,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";

import { container, item, Layout } from "~/components/site/layout";
import type { CurrentAuthor } from "~/lib/authors";
import { CURRENT_TEAM } from "~/lib/authors";
import { ROUTE_DATA } from "~/lib/constants";
import { getMeta } from "~/lib/get-meta";
import { getUrls } from "~/lib/get-urls";

const { desc, path, title } = ROUTE_DATA.about;

export function loader({ request }: LoaderFunctionArgs) {
  return {
    urls: getUrls({ path, request }),
  };
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return data ? getMeta({ desc, title, urls: data.urls }) : [];
};

export type Content = Readonly<{
  icon: IconDefinition;
  title: string;
  text: string;
  link?: string;
}>;

const values: Content[] = [
  {
    icon: faArrowsMaximize,
    title: "Access is opportunity",
    text: "We strive to provide access to the broadest market possible.",
  },
  {
    icon: faHexagonXmark,
    title: "Reduce distractions",
    text: "We aim to make new technologies easier to use.",
  },

  {
    icon: faEarthAmericas,
    title: "A future open to all",
    text: "We aim to build technology that can be used without needing to understand all of its complexities.",
  },
  {
    icon: faCodeBranch,
    title: "Embrace experimentation",
    text: "We are motivated by curiosity and innovation, allowing us to create and iterate the next best thing.",
  },
];

export default function About(): JSX.Element {
  const [shuffledPortraits, setShuffledPortraits] = useState<CurrentAuthor[]>(
    []
  );

  useEffect(() => {
    shufflePortraits();
  }, []);

  const shufflePortraits = () => {
    const teamArr = Object.values(CURRENT_TEAM);
    const shuffled = [...teamArr].sort(() => Math.random() - 0.5);
    setShuffledPortraits(shuffled);
  };

  return (
    <Layout>
      <section className="section-first">
        <motion.div variants={item} className="container">
          <h1 className="eyebrow">About</h1>
          <div className="lg:grid lg:grid-cols-2 items-start gap-16">
            <h2 className="m-0 mb-6">Create the future together</h2>
            <p className="subheading mt-2 mb-0">
              Brale enables financial innovations of tomorrow built on the
              regulatory frameworks of today.
            </p>
          </div>
        </motion.div>
      </section>
      <section className="section-sub">
        <div className="container lg:grid lg:grid-cols-2 items-start gap-16">
          <motion.h2 variants={item} className="m-0 lg:sticky top-8 mb-6">
            Our values guide us
          </motion.h2>
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.p variants={item} className="subheading mt-2 mb-16">
              As a small, distributed team, we are led by our values. These core
              beliefs ensure a shared vision internally and with an alum network
              as we build the future together.
            </motion.p>
            <motion.div
              variants={item}
              className="flex flex-col col-span-2 gap-y-8"
            >
              <motion.div
                variants={container}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="flex flex-col sm:grid sm:grid-cols-2 blurb-gap"
              >
                {values.map((c) => (
                  <motion.div variants={item} key={c.title}>
                    <FontAwesomeIcon
                      className="h-12 w-12 mb-3 text-purple-500"
                      icon={c.icon}
                    />
                    <h4 className="mt-0 mb-3">{c.title}</h4>
                    <p className="blurb m-0">{c.text}</p>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>
      <section className="section-sub">
        <div className="container">
          <div className="lg:grid lg:grid-cols-2 items-start gap-16 mb-16">
            <motion.div variants={item}>
              <h2 className="m-0 mb-6">Our team</h2>
              <p className="subheading">
                Here to build what’s next, bit by bit.
              </p>
            </motion.div>
          </div>
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-6 sm:gap-8 mb-12"
          >
            {shuffledPortraits.map((p) => (
              <motion.img
                variants={item}
                key={p.name}
                className="m-0 round"
                src={p.portrait}
                alt={`${p.name} portrait`}
              />
            ))}
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}

export function ContentIcon({ content }: { content: Content }): JSX.Element {
  return (
    <FontAwesomeIcon
      className="h-12 w-12 mb-5 text-purple-500"
      icon={content.icon}
    />
  );
}
